import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import { defaults } from 'ol/control/defaults';
import { extend, createEmpty } from 'ol/extent';
import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj';
import { Vector as VectorSource, XYZ } from 'ol/source';
import View from 'ol/View';
import React, { useRef, useMemo } from 'react';

import styles from './MissionPreview.module.scss';

import EmptyList from '@/components/ui/EmptyList';
import { STYLES } from '@/define/olMaps';
import { createFenceFeatures } from '@/utils/FenceUtil';
import { getDistanceAlongPath } from '@/utils/MapUtils';

const cx = classNames.bind(styles);

const MissionPreview = ({ mission }) => {
  const map = useRef();
  const mapRef = useRef();
  const layer = useRef();

  // Map 초기화
  useMountEffect(() => {
    if (!mapRef.current) return;

    map.current = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({ url: `https://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}` }),
        }),
      ],
      view: new View({ maxZoom: 18 }),
      controls: defaults({ attribution: false, rotate: false, zoom: false }),
    });
  });

  useUpdateEffect(() => {
    // 기존 레이어 제거
    if (layer.current) {
      map.current.removeLayer(layer.current);
    }

    if (!mission) return;

    //#region 경로 정의
    const path = getPathFromJSON(mission.json).map(({ lat, lng }) => fromLonLat([lng, lat]));
    // 경로 정의
    const line = new Feature(new LineString(path));
    // 경로 Style 정의
    line.setStyle(STYLES.MISSION.PATH);
    //#endregion

    //#region 화살표 정의
    const arrow = new Feature(new Point(path.at(-1)));
    // 화살표 방향 정의
    let heading = 0;
    if (1 < path.length) {
      const [x1, y1] = path.at(-2);
      const [x2, y2] = path.at(-1);
      heading = Math.atan2(y2 - y1, x2 - x1) - Math.PI / 4;
    }
    // 화살표 Style 정의
    arrow.setStyle(STYLES.MISSION.ARROW(-heading));
    //#endregion

    //#region Fence 정의
    const fences = createFenceFeatures(mission.json.fences);
    fences.forEach((fence) => {
      fence.setStyle(STYLES.MISSION.FENCE);
    });
    //#endregion

    const source = new VectorSource({ features: [line, arrow, ...fences] });
    layer.current = new VectorLayer({ source });
    map.current.addLayer(layer.current);

    //#region 맵 중심 조정
    const allGeometries = layer.current
      .getSource()
      .getFeatures()
      .map((feature) => feature.getGeometry());

    const combinedExtent = allGeometries.reduce((extent, geometry) => {
      return extend(extent, geometry.getExtent());
    }, createEmpty());

    map.current.getView().fit(combinedExtent, { padding: [40, 40, 40, 40] });
    //#endregion
  }, [mission]);

  const getPathFromJSON = (json) => {
    const path = [];
    json.commands.forEach(({ type, data }) => {
      switch (type) {
        case 'navLand':
        case 'navLoiterToAlt':
        case 'navTakeoff':
        case 'navWaypoint':
          path.push(data.position);
          break;

        case 'navReturnToLaunch':
          path.push(json.commands[0].data.position);
          break;

        case 'cusSurvey':
          data.positions.forEach((position) => path.push(position));
          break;

        default:
          break;
      }
    });
    return path;
  };

  const stats = useMemo(() => {
    if (!mission) return;

    const { commands } = mission.json;

    // Speed
    const speeds = commands.filter(({ data }) => data?.speed).map(({ data }) => data.speed);
    const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : null;
    const minSpeed = speeds.length > 0 ? Math.min(...speeds) : null;

    // Altitude
    const altitudes = commands.filter(({ data }) => data?.altitude).map(({ data }) => data.altitude);
    const maxAltitude = Math.max(...altitudes);
    const minAltitude = Math.min(...altitudes);

    // Distance
    const path = getPathFromJSON(mission.json);
    const totalDistance = getDistanceAlongPath(path);

    return {
      maxSpeed,
      minSpeed,
      maxAltitude,
      minAltitude,
      totalDistance,
    };
  }, [mission]);

  return (
    <div className={cx('container')}>
      <div ref={mapRef} className={cx(['map', { show: mission !== undefined }])} />
      {!mission && (
        <div className={cx('empty')}>
          <EmptyList message="No item selected" />
        </div>
      )}
      {mission && (
        <div className={cx('info')}>
          {stats.maxSpeed !== null && (
            <div className={cx('item')}>
              <div className={cx('label')}>Max Speed</div>
              <div className={cx('value')}>
                {stats.maxSpeed.toFixed(1)}
                <span>m/s</span>
              </div>
            </div>
          )}
          {stats.minSpeed !== null && (
            <div className={cx('item')}>
              <div className={cx('label')}>Min Speed</div>
              <div className={cx('value')}>
                {stats.minSpeed.toFixed(1)}
                <span>m/s</span>
              </div>
            </div>
          )}
          <div className={cx('item')}>
            <div className={cx('label')}>Max Altitude</div>
            <div className={cx('value')}>
              {stats.maxAltitude.toFixed(1)}
              <span>m</span>
            </div>
          </div>
          <div className={cx('item')}>
            <div className={cx('label')}>Min Altitude</div>
            <div className={cx('value')}>
              {stats.minAltitude.toFixed(1)}
              <span>m</span>
            </div>
          </div>
          <div className={cx('item')}>
            <div className={cx('label')}>Distance</div>
            <div className={cx('value')}>
              {commaNumber((stats.totalDistance / 1000).toFixed(1))}
              <span>km</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionPreview;
